// useEnumOptionEditor.js

import { useCallback, useEffect, useMemo, useState } from 'react';
import { isEnumCompatibleFieldType, isOptionsListFieldType } from 'utilities/FieldTypes';

import {
  createEnumGroupId,
  createEnumOptionId,
  generateEnumOptionValue,
  getOptionLabel,
  isEnumOptionGroup,
  normalizeEnumOptionValues,
  normalizeEnumOptions,
} from 'utilities/EnumOptions';
import { normalizeMeasurementDefaultUnit } from 'utilities/Measurement';

/**
 * Manages enum option editing state and handlers for the registry field editor.
 * Option values are auto-generated from labels (admins do not edit values directly).
 * Newly added options keep value in sync with label until the option is locked;
 * existing options keep a stable value across renames.
 * Group headers (`kind: 'group'`) can be interleaved; removing a group keeps its options.
 */
const useEnumOptionEditor = ({ selected, selectedIndex, patchSelected }) => {
  const [optionLabelDrafts, setOptionLabelDrafts] = useState({});
  const [valueAutoSyncById, setValueAutoSyncById] = useState({});

  const hasSelected = Boolean(selected);
  const isMeasurementField = selected?.type === 'measurement';
  const isOptionsField = isOptionsListFieldType(selected) || isMeasurementField;
  const supportsGroups = isOptionsListFieldType(selected);
  const selectedOptions = selected?.options;

  const selectedEnumOptions = useMemo(() => (
    isOptionsField ? normalizeEnumOptions(selectedOptions) : []
  ), [isOptionsField, selectedOptions]);

  useEffect(() => {
    if (!hasSelected || !isOptionsField) {
      setOptionLabelDrafts((prev) => (prev && Object.keys(prev).length ? {} : prev));
      setValueAutoSyncById((prev) => (prev && Object.keys(prev).length ? {} : prev));
      return;
    }

    const optionsArray = normalizeEnumOptions(selectedOptions);

    setOptionLabelDrafts((prev) => {
      const next = {};
      optionsArray.forEach((option) => {
        const optionId = option?.id || '';
        if (!optionId) return;
        if (prev && Object.prototype.hasOwnProperty.call(prev, optionId)) {
          next[optionId] = prev[optionId];
        }
      });
      const prevKeys = Object.keys(prev || {});
      const nextKeys = Object.keys(next);
      if (prevKeys.length === nextKeys.length && nextKeys.every((key) => prev?.[key] === next[key])) {
        return prev;
      }
      if (prevKeys.length === 0 && nextKeys.length === 0) return prev || {};
      return next;
    });

    // Existing options stay locked. Only newly added ids (marked true on add) keep syncing.
    setValueAutoSyncById((prev) => {
      const next = {};
      optionsArray.forEach((option) => {
        const optionId = option?.id || '';
        if (!optionId || isEnumOptionGroup(option)) return;
        next[optionId] = prev?.[optionId] === true;
      });
      const prevKeys = Object.keys(prev || {});
      const nextKeys = Object.keys(next);
      if (
        prevKeys.length === nextKeys.length
        && nextKeys.every((key) => prev?.[key] === next[key])
      ) {
        return prev;
      }
      return next;
    });
  }, [hasSelected, isOptionsField, selectedOptions]);

  // Leaving the field locks values so later label renames stay stable.
  useEffect(() => {
    setValueAutoSyncById((prev) => {
      if (!prev || !Object.keys(prev).length) return prev;
      let changed = false;
      const next = {};
      Object.keys(prev).forEach((optionId) => {
        if (prev[optionId] === true) changed = true;
        next[optionId] = false;
      });
      return changed ? next : prev;
    });
  }, [selectedIndex]);

  const uniqueValue = useCallback((desired, existingValues) => {
    let candidate = desired || 'option';
    if (!existingValues.has(candidate)) return candidate;
    let counter = 2;
    while (existingValues.has(`${candidate}_${counter}`)) counter += 1;
    return `${candidate}_${counter}`;
  }, []);

  const handleOptionAdd = useCallback(() => {
    if (selectedIndex < 0 || !selected || !isOptionsField) return;
    const existing = normalizeEnumOptions(selected.options);
    const baseLabel = 'Option';
    let counter = existing.filter((opt) => !isEnumOptionGroup(opt)).length + 1;
    let label = `${baseLabel} ${counter}`;
    const existingLabels = new Set(
      existing.filter((opt) => !isEnumOptionGroup(opt)).map((opt) => getOptionLabel(opt))
    );
    while (existingLabels.has(label)) {
      counter += 1;
      label = `${baseLabel} ${counter}`;
    }
    const existingValues = new Set(
      existing.filter((opt) => !isEnumOptionGroup(opt)).map((opt) => opt.value)
    );
    const value = uniqueValue(generateEnumOptionValue(label), existingValues);
    const optionId = createEnumOptionId();
    patchSelected({
      options: [
        ...existing,
        {
          id: optionId,
          value,
          translations: { en: { label } },
          color: undefined,
          iconKey: undefined,
          note: undefined,
          system: true,
          sortOrder: existing.length,
        },
      ],
    });
    setValueAutoSyncById((prev) => ({ ...(prev || {}), [optionId]: true }));
  }, [selectedIndex, selected, patchSelected, isOptionsField, uniqueValue]);

  const handleGroupAdd = useCallback(() => {
    if (selectedIndex < 0 || !selected || !supportsGroups) return;
    const existing = normalizeEnumOptions(selected.options);
    const baseLabel = 'Group';
    let counter = existing.filter((opt) => isEnumOptionGroup(opt)).length + 1;
    let label = `${baseLabel} ${counter}`;
    const existingLabels = new Set(
      existing.filter((opt) => isEnumOptionGroup(opt)).map((opt) => getOptionLabel(opt))
    );
    while (existingLabels.has(label)) {
      counter += 1;
      label = `${baseLabel} ${counter}`;
    }
    const groupId = createEnumGroupId();
    patchSelected({
      options: [
        ...existing,
        {
          kind: 'group',
          id: groupId,
          value: `__group__${groupId}`,
          translations: { en: { label } },
          sortOrder: existing.length,
        },
      ],
      enumOptionsSort: 'manual',
    });
  }, [selectedIndex, selected, patchSelected, supportsGroups]);

  const handleOptionRemove = useCallback((optionId) => {
    if (selectedIndex < 0 || !selected || !isOptionsField) return;
    const existing = normalizeEnumOptions(selected.options);
    const target = existing.find((opt) => opt.id === optionId);
    if (!target) return;

    const optionValue = target.value;
    const nextOptions = existing.filter((opt) => opt.id !== optionId);
    const patch = { options: nextOptions };
    if (!isEnumOptionGroup(target)) {
      if (selected.default === optionValue) {
        patch.default = undefined;
      } else if (Array.isArray(selected.default) && selected.default.includes(optionValue)) {
        patch.default = selected.default.filter((entry) => entry !== optionValue);
      }
      if (isMeasurementField) {
        patch.defaultUnit = normalizeMeasurementDefaultUnit(selected.defaultUnit, nextOptions);
      }
    }
    patchSelected(patch);
    setOptionLabelDrafts((prev) => {
      if (!prev || !Object.prototype.hasOwnProperty.call(prev, optionId)) return prev;
      const next = { ...prev };
      delete next[optionId];
      return next;
    });
    setValueAutoSyncById((prev) => {
      if (!prev || !Object.prototype.hasOwnProperty.call(prev, optionId)) return prev;
      const next = { ...prev };
      delete next[optionId];
      return next;
    });
  }, [selectedIndex, selected, patchSelected, isMeasurementField, isOptionsField]);

  const handleOptionLabelCommit = useCallback((optionId) => {
    if (selectedIndex < 0 || !selected || !isOptionsField) return;

    const normalized = normalizeEnumOptions(selected.options);
    const targetOption = normalized.find((option) => option.id === optionId);
    if (!targetOption) return;

    const currentLabel = getOptionLabel(targetOption);
    const hasDraft = optionLabelDrafts && Object.prototype.hasOwnProperty.call(optionLabelDrafts, optionId);
    const rawDraft = hasDraft ? optionLabelDrafts[optionId] : currentLabel;
    const trimmed = String(rawDraft || '').trim();

    if (!trimmed) {
      setOptionLabelDrafts((prev) => ({
        ...(prev || {}),
        [optionId]: currentLabel,
      }));
      return;
    }

    const isGroup = isEnumOptionGroup(targetOption);
    const shouldAutoSync = !isGroup && valueAutoSyncById?.[optionId] === true;
    const existingValues = new Set(
      normalizeEnumOptionValues(selected.options).filter((val) => val !== targetOption.value)
    );

    let nextValue = targetOption.value;
    if (shouldAutoSync) {
      nextValue = uniqueValue(generateEnumOptionValue(trimmed), existingValues);
    }

    const nextOptions = normalized.map((opt) => {
      if (opt.id !== optionId) return opt;
      if (isGroup) {
        return {
          ...opt,
          kind: 'group',
          value: `__group__${opt.id}`,
          translations: {
            ...(opt.translations || {}),
            en: { label: trimmed },
          },
        };
      }
      return {
        ...opt,
        value: nextValue,
        translations: {
          ...(opt.translations || {}),
          en: { label: trimmed },
        },
      };
    });

    const patch = { options: nextOptions };
    if (shouldAutoSync && nextValue !== targetOption.value) {
      if (selected.default === targetOption.value) {
        patch.default = nextValue;
      } else if (Array.isArray(selected.default) && selected.default.includes(targetOption.value)) {
        patch.default = selected.default.map((entry) => (
          entry === targetOption.value ? nextValue : entry
        ));
      }
      if (isMeasurementField && selected.defaultUnit === targetOption.value) {
        patch.defaultUnit = nextValue;
      }
    }

    patchSelected(patch);
    setOptionLabelDrafts((prev) => {
      if (!prev || !Object.prototype.hasOwnProperty.call(prev, optionId)) return prev;
      const next = { ...prev };
      delete next[optionId];
      return next;
    });
  }, [
    selectedIndex,
    selected,
    optionLabelDrafts,
    valueAutoSyncById,
    patchSelected,
    isMeasurementField,
    isOptionsField,
    uniqueValue,
  ]);

  const handleOptionNoteChange = useCallback((optionId, nextNote) => {
    if (selectedIndex < 0 || !selected || !isOptionsField) return;
    const normalized = normalizeEnumOptions(selected.options);
    const target = normalized.find((opt) => opt.id === optionId);
    if (!target || isEnumOptionGroup(target)) return;
    const raw = typeof nextNote === 'string' ? nextNote : '';
    const nextOptions = normalized.map((opt) => (
      opt.id === optionId
        ? { ...opt, note: raw.trim().length ? raw : undefined }
        : opt
    ));
    patchSelected({ options: nextOptions });
  }, [selectedIndex, selected, patchSelected, isOptionsField]);

  const handleOptionTargetDurationChange = useCallback((optionId, nextDuration) => {
    if (selectedIndex < 0 || !selected || selected?.type !== 'pipeline') return;
    const normalized = normalizeEnumOptions(selected.options);
    const target = normalized.find((opt) => opt.id === optionId);
    if (!target || isEnumOptionGroup(target)) return;

    const raw = typeof nextDuration === 'string' ? nextDuration.trim() : nextDuration;
    let targetDuration;
    if (raw === '' || raw == null) {
      targetDuration = undefined;
    } else {
      const numeric = Number(raw);
      if (!Number.isFinite(numeric) || numeric <= 0) {
        targetDuration = undefined;
      } else {
        targetDuration = Math.round(numeric);
      }
    }

    const nextOptions = normalized.map((opt) => {
      if (opt.id !== optionId) return opt;
      if (targetDuration == null) {
        const { targetDuration: _removed, ...rest } = opt;
        return rest;
      }
      return { ...opt, targetDuration };
    });
    patchSelected({ options: nextOptions });
  }, [selectedIndex, selected, patchSelected]);

  const handleOptionColorChange = useCallback((optionId, nextColor) => {
    if (selectedIndex < 0 || !selected || !isOptionsField) return;

    const normalized = normalizeEnumOptions(selected.options);
    const target = normalized.find((opt) => opt.id === optionId);
    if (!target || isEnumOptionGroup(target)) return;
    const nextOptions = normalized.map((opt) => (
      opt.id === optionId
        ? { ...opt, color: nextColor || undefined }
        : opt
    ));

    patchSelected({ options: nextOptions });
  }, [selectedIndex, selected, patchSelected, isOptionsField]);

  const handleOptionIconChange = useCallback((optionId, nextIconKey) => {
    if (selectedIndex < 0 || !selected || !isOptionsField) return;

    const normalizedIconKey = typeof nextIconKey === 'string'
      ? nextIconKey.trim()
      : '';
    const normalized = normalizeEnumOptions(selected.options);
    const target = normalized.find((opt) => opt.id === optionId);
    if (!target || isEnumOptionGroup(target)) return;
    const nextOptions = normalized.map((opt) => (
      opt.id === optionId
        ? { ...opt, iconKey: normalizedIconKey || undefined }
        : opt
    ));

    patchSelected({ options: nextOptions });
  }, [selectedIndex, selected, patchSelected, isOptionsField]);

  const handleOptionMoveByDelta = useCallback((optionId, delta) => {
    if (selectedIndex < 0 || !selected || !isOptionsField) return;
    const normalized = normalizeEnumOptions(selected.options);
    const fromIndex = normalized.findIndex((option) => option.id === optionId);
    if (fromIndex < 0) return;
    const toIndex = fromIndex + delta;
    if (toIndex < 0 || toIndex >= normalized.length) return;

    const nextOptions = [...normalized];
    const [moved] = nextOptions.splice(fromIndex, 1);
    nextOptions.splice(toIndex, 0, moved);
    patchSelected({
      options: nextOptions.map((opt, index) => ({ ...opt, sortOrder: index })),
      enumOptionsSort: 'manual',
    });
  }, [selectedIndex, selected, patchSelected, isOptionsField]);

  const handleOptionLabelKeyDown = useCallback((optionId, event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleOptionLabelCommit(optionId);
      event.currentTarget.blur();
    }
  }, [handleOptionLabelCommit]);

  return {
    selectedEnumOptions,
    optionLabelDrafts,
    setOptionLabelDrafts,
    supportsGroups,
    handleOptionAdd,
    handleGroupAdd,
    handleOptionRemove,
    handleOptionLabelCommit,
    handleOptionLabelKeyDown,
    handleOptionNoteChange,
    handleOptionTargetDurationChange,
    handleOptionColorChange,
    handleOptionIconChange,
    handleOptionMoveByDelta,
  };
};

export default useEnumOptionEditor;
